import React, { useRef } from "react";
import styled from "styled-components";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { ease, font } from "../tokens";
import { Inner, STATIC_MODE, useReveal } from "../primitives";
import TradingCard from "../TradingCard";
import { ZIP_RETURN_SECTION } from "../data";
import EraHeader from "./EraHeader";

const Wrap = styled.div`
  padding-bottom: clamp(120px, 22vh, 240px);
`;

const Stage = styled.div`
  position: relative;
  margin-top: clamp(40px, 7vh, 80px);
`;

/* Outlined ghost words drift in opposite directions behind the card. */
const Ghost = styled(motion.span)<{ $top: string; $align: "left" | "right" }>`
  position: absolute;
  top: ${(p) => p.$top};
  ${(p) => (p.$align === "left" ? "left: 0;" : "right: 0;")}
  font-family: ${font.display};
  font-size: clamp(64px, 11vw, 180px);
  font-weight: 400;
  line-height: 1;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  color: transparent;
  -webkit-text-stroke: 1.5px rgba(14, 14, 12, 0.18);
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
  will-change: transform;
`;

const CardWrap = styled.div`
  position: relative;
  max-width: 460px;
  margin: 0 auto;
  padding: clamp(40px, 7vh, 90px) 0;
  perspective: 1200px;
  z-index: 1;
`;

/** Section — 03 Zip Co return (Jan–May 2024): the comeback season. */
const ZipReturnSection: React.FC = () => {
  const reduced = useReducedMotion();
  const { ref, shown } = useReveal();
  const stageRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: stageRef,
    offset: ["start end", "end start"],
  });
  const driftLeft = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const driftRight = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <Wrap data-era={2}>
      <Inner>
        <EraHeader
          era={ZIP_RETURN_SECTION}
          statementInk="Back at Zip for a season."
          statementMuted="Five months deep in .NET microservices, Microsoft SQL and BAU across Angular and React."
        />
        <Stage ref={stageRef}>
          <Ghost
            $top="4%"
            $align="left"
            style={STATIC_MODE ? undefined : { x: driftLeft }}
            aria-hidden
          >
            Return
          </Ghost>
          <Ghost
            $top="62%"
            $align="right"
            style={STATIC_MODE ? undefined : { x: driftRight }}
            aria-hidden
          >
            2024
          </Ghost>
          <CardWrap ref={ref}>
            <motion.div
              initial={false}
              animate={
                shown
                  ? { opacity: 1, x: 0, rotate: 1.4, rotateY: 0 }
                  : {
                      opacity: 0,
                      x: reduced ? 0 : 80,
                      rotate: reduced ? 0 : 7,
                      rotateY: reduced ? 0 : 55,
                    }
              }
              transition={{ duration: 1, ease: ease.out }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <TradingCard card={ZIP_RETURN_SECTION.cards[0]} />
            </motion.div>
          </CardWrap>
        </Stage>
      </Inner>
    </Wrap>
  );
};

export default ZipReturnSection;
